// The signal shape the picker describes and the block placed on the heat.
//
// The shape is raw LoRa parameters: bandwidth, spreading factor, coding rate
// and payload size. A matching Meshtastic preset is named for orientation
// but nothing depends on it.

import { MESHTASTIC_PRESETS, type MeshtasticPreset } from './builtin/meshtastic.ts'
import { timeOnAirS } from './lora.ts'

/** The LoRa bandwidths a Semtech radio offers in this band, in Hz. */
export const LORA_BANDWIDTHS_HZ: readonly number[] = [62.5e3, 125e3, 250e3, 500e3]
export const LORA_SPREADING_FACTORS: readonly number[] = [5, 6, 7, 8, 9, 10, 11, 12]
/** Coding rate denominators for 4/5 through 4/8. */
export const LORA_CODING_RATES: readonly number[] = [5, 6, 7, 8]

export interface SignalSettings {
  signalBwHz: number
  signalSf: number
  signalCr: number
  payloadBytes: number
}

export interface SignalShape {
  widthHz: number
  airtimeS: number
  sf: number
  cr: number
  /** The Meshtastic preset with these parameters, if any. Named only; nothing depends on it. */
  preset: MeshtasticPreset | null
}

export function signalShape(s: SignalSettings): SignalShape {
  const preset = MESHTASTIC_PRESETS.find((p) => p.bwHz === s.signalBwHz && p.sf === s.signalSf && p.cr === s.signalCr) ?? null
  return {
    widthHz: s.signalBwHz,
    airtimeS: timeOnAirS({ bwHz: s.signalBwHz, sf: s.signalSf, cr: s.signalCr, payloadBytes: s.payloadBytes }),
    sf: s.signalSf,
    cr: s.signalCr,
    preset,
  }
}

/** A block on the heat: center frequency, and when it starts. */
export interface PlacedSignal {
  centerHz: number
  startMs: number
}

export function describeShape(shape: SignalShape): string {
  const width = shape.widthHz >= 1e6 ? `${(shape.widthHz / 1e6).toFixed(3)} MHz` : `${shape.widthHz / 1e3} kHz`
  const airtime = shape.airtimeS >= 1 ? `${shape.airtimeS.toFixed(2)} s` : `${Math.round(shape.airtimeS * 1000)} ms`
  const preset = shape.preset ? ` (Meshtastic ${shape.preset.name})` : ''
  return `${width} × ${airtime}, SF${shape.sf} CR 4/${shape.cr}${preset}`
}
